/**
 * One-time rename of the browser-storage keys this application inherited.
 *
 * The keys below hold a signed-in session, a scoped agency selection and a
 * chosen theme. They live in the visitor's browser, so renaming them is not a
 * string change: a straight rename signs everyone out, drops whichever client
 * they had selected, and repaints the page in the wrong theme on the next
 * reload. This module copies each value across instead, once, before anything
 * reads it.
 *
 * Five rules, each of which is a way the obvious version loses something:
 *
 * 1. **Copy, never move.** The legacy key is left exactly as it was. A deploy
 *    that gets rolled back then still finds the session it wrote; deleting the
 *    source is what makes a rollback a logout.
 * 2. **Never overwrite.** A value already under the current key is newer by
 *    definition -- this code only ever runs before the current key exists --
 *    so a present target means the migration already happened and the legacy
 *    value is stale. It is skipped, not merged.
 * 3. **Idempotent by construction.** Rule 2 makes the second run a no-op. There
 *    is no "migrated" flag to get out of step with the data it describes.
 *    Running it on every start is therefore safe and is what makes it work for
 *    a visitor who has not opened the app since before the rename.
 * 4. **Never throws.** `localStorage` itself throws when site data is blocked,
 *    and a storage access is not worth a blank page. Every read and write is
 *    guarded individually, so one bad key cannot abandon the rest.
 * 5. **Theme is handled twice.** The copy that matters for the theme is the
 *    inline script in `index.html`, which runs before the first paint; this
 *    module repeats it for completeness. Doing it only here would be a visible
 *    flash of the wrong ground colour on the first reload after the rename.
 *
 * Call it once from the entry module, before React renders.
 */

/** Exact key renames: `[legacy, current]`. */
export const LEGACY_KEY_RENAMES: ReadonlyArray<readonly [string, string]> = [
  // The signed-in session. First on the list because it is the one whose loss
  // a person notices immediately.
  ['rivo9_auth', 'xtremand_auth'],
  ['rivo9_scoped_agency_token', 'xtremand_scoped_agency_token'],
  ['rivo9_scoped_agency_refresh_token', 'xtremand_scoped_agency_refresh_token'],
  // Theme. Also migrated by the pre-paint script in index.html -- see rule 5.
  ['rivo9-theme', 'xtremand-theme'],
  ['rivo9-theme-category', 'xtremand-theme-category'],
  // The read-only support-session banner.
  ['rivo9.supportSession', 'xtremand.supportSession'],
] as const;

/**
 * Prefix renames, for families of keys written one per selection rather than
 * under one fixed name -- `rivo9-theme-<id>`, of which there is one per
 * whatever the visitor has themed. The suffix is preserved exactly.
 *
 * `rivo9-theme-category` matches this prefix as well as being an exact rename
 * above; both produce the same target, and rule 2 means whichever runs second
 * does nothing.
 */
export const LEGACY_PREFIX_RENAMES: ReadonlyArray<readonly [string, string]> = [
  ['rivo9-theme-', 'xtremand-theme-'],
] as const;

export interface StorageMigrationResult {
  /** Keys copied to their new name. */
  migrated: number;
  /** Legacy keys skipped because the new name already held a value. */
  skipped: number;
  /** Keys that could not be read or written. */
  failed: number;
}

const EMPTY: StorageMigrationResult = { migrated: 0, skipped: 0, failed: 0 };

function read(storage: Storage, key: string): string | null | undefined {
  try {
    return storage.getItem(key);
  } catch {
    return undefined; // distinct from null: "could not read", not "not set"
  }
}

function copyKey(
  storage: Storage,
  legacy: string,
  current: string,
  out: StorageMigrationResult,
): void {
  const legacyValue = read(storage, legacy);
  if (legacyValue === undefined) {
    out.failed += 1;
    return;
  }
  if (legacyValue === null) return; // nothing stored under the old name

  const currentValue = read(storage, current);
  if (currentValue === undefined) {
    out.failed += 1;
    return;
  }
  if (currentValue !== null) {
    out.skipped += 1; // rule 2
    return;
  }

  try {
    storage.setItem(current, legacyValue);
    out.migrated += 1;
  } catch {
    // Quota, or a storage that reads but refuses to write. The legacy key is
    // untouched, so the next start tries again.
    out.failed += 1;
  }
}

/** Every key currently in `storage`, or `null` if it cannot be enumerated. */
function keysOf(storage: Storage): string[] | null {
  try {
    const keys: string[] = [];
    for (let i = 0; i < storage.length; i += 1) {
      const key = storage.key(i);
      if (key !== null) keys.push(key);
    }
    return keys;
  } catch {
    return null;
  }
}

/** Migrate one storage area. Never throws. */
export function migrateLegacyStorage(storage: Storage): StorageMigrationResult {
  const out: StorageMigrationResult = { ...EMPTY };

  for (const [legacy, current] of LEGACY_KEY_RENAMES) {
    copyKey(storage, legacy, current, out);
  }

  const keys = keysOf(storage);
  if (keys === null) {
    out.failed += 1;
    return out;
  }

  for (const [legacyPrefix, currentPrefix] of LEGACY_PREFIX_RENAMES) {
    for (const key of keys) {
      if (!key.startsWith(legacyPrefix)) continue;
      copyKey(storage, key, currentPrefix + key.slice(legacyPrefix.length), out);
    }
  }

  return out;
}

/**
 * Migrate both storage areas. Safe to call on every start, and safe to call
 * where `window` does not exist.
 */
export function runLegacyStorageMigration(): StorageMigrationResult {
  if (typeof window === 'undefined') return { ...EMPTY };

  const total: StorageMigrationResult = { ...EMPTY };
  for (const area of ['localStorage', 'sessionStorage'] as const) {
    let storage: Storage;
    try {
      // The ACCESSOR throws when site data is blocked, before any method on it
      // is called -- which is why this is a separate try from the ones inside
      // migrateLegacyStorage.
      storage = window[area];
    } catch {
      total.failed += 1;
      continue;
    }
    const result = migrateLegacyStorage(storage);
    total.migrated += result.migrated;
    total.skipped += result.skipped;
    total.failed += result.failed;
  }
  return total;
}
